import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  MapPin,
  Users,
  Building,
  Heart,
  Briefcase,
  Trash2,
  Edit,
  Repeat,
  AlertTriangle,
  Filter,
  X,
  Loader2,
  Printer,
  Phone,
  RefreshCw
} from "lucide-react";
import PrintableDayPlanner from "@/components/PrintableDayPlanner";
import ReflectionCard from "@/components/ReflectionCard";
import AOCManager from "@/components/AOCManager";
import {
  buildFacilityColorMap,
  getMeetingColor,
  findAllConflictingIds,
  layoutDayMeetings,
  PERSONAL_COLOR,
  CONFLICT_COLOR,
} from "@/lib/calendarUtils";

// Time formatting helper
const formatHour = (hour) => {
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const displayH = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${displayH}:${m.toString().padStart(2, '0')} ${ampm}`;
};

// Generate time options for select
const TIME_OPTIONS = [];
for (let h = 6; h <= 22; h++) {
  for (let m = 0; m < 60; m += 15) {
    const value = h + m / 60;
    const displayH = h > 12 ? h - 12 : h === 0 ? 12 : h;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const label = `${displayH}:${m.toString().padStart(2, '0')} ${ampm}`;
    TIME_OPTIONS.push({ value, label });
  }
}

// Calendar view types
const VIEW_TYPES = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month'
};

// Default facility colors
const DEFAULT_COLORS = {
  personal: '#10B981',
  work: '#6B7280'
};

export default function CalendarPage() {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [aocShifts, setAocShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  // Default to Day view on mobile — the 7-col Week grid is unreadable at <768px.
  const [viewType, setViewType] = useState(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      return VIEW_TYPES.DAY;
    }
    return VIEW_TYPES.WEEK;
  });
  const [selectedFacility, setSelectedFacility] = useState("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showPrintPlanner, setShowPrintPlanner] = useState(false);
  const [printDate, setPrintDate] = useState(null);

  // Bill overlay (Personal Budget) — opt-in toggle on Month view.
  const [showBills, setShowBills] = useState(() => {
    try { return localStorage.getItem("niko_show_bills") === "1"; } catch { return false; }
  });
  const [billsByDate, setBillsByDate] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    date: new Date().toISOString().split('T')[0],
    startTime: 9,
    endTime: 10,
    facility_id: "",
    category: "work",
    recurring: "",
    recurring_end_date: "",
    importance: "medium",
    location: "",
    notes: "",
    attendees: ""
  });

  // Calculate date range based on view
  const getDateRange = useCallback(() => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);
    
    if (viewType === VIEW_TYPES.DAY) {
      // Single day
    } else if (viewType === VIEW_TYPES.WEEK) {
      // Week view - start from Sunday
      const dayOfWeek = start.getDay();
      start.setDate(start.getDate() - dayOfWeek);
      end.setDate(start.getDate() + 6);
    } else if (viewType === VIEW_TYPES.MONTH) {
      // Month view
      start.setDate(1);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
    }
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  }, [currentDate, viewType]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { start, end } = getDateRange();
      
      const [meetingsRes, facilitiesRes, tasksRes, aocRes] = await Promise.all([
        axios.get(`${API}/meetings/calendar?start_date=${start}&end_date=${end}`, { withCredentials: true }),
        axios.get(`${API}/facilities`, { withCredentials: true }),
        axios.get(`${API}/tasks`, { withCredentials: true }),
        axios.get(`${API}/aoc?start_date=${start}&end_date=${end}`, { withCredentials: true })
      ]);
      
      setMeetings(meetingsRes.data || []);
      setFacilities(facilitiesRes.data || []);
      setTasks(tasksRes.data || []);
      setAocShifts(aocRes.data || []);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
      toast.error("Failed to load calendar");
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh when the user returns to the tab/window. Cheaper than
  // polling and matches the typical pattern: edit a task on /tasks, switch
  // back to /calendar, see the change immediately. Also fires when the
  // browser tab is brought back into the foreground.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") fetchData();
    };
    window.addEventListener("focus", fetchData);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", fetchData);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchData]);

  // Fetch bill overlay for the visible month when toggle is ON. Cheap call
  // (just user's recurring bills materialized for the chosen month) and we
  // group by date for O(1) lookup in the cell renderer.
  useEffect(() => {
    if (!showBills || viewType !== VIEW_TYPES.MONTH) {
      setBillsByDate({});
      return;
    }
    const y = currentDate.getFullYear();
    const m = String(currentDate.getMonth() + 1).padStart(2, "0");
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/budget/calendar?month=${y}-${m}`,
          { withCredentials: true },
        );
        if (cancelled) return;
        const grouped = {};
        (res.data || []).forEach((b) => {
          if (!grouped[b.date]) grouped[b.date] = [];
          grouped[b.date].push(b);
        });
        setBillsByDate(grouped);
      } catch (_) {
        if (!cancelled) setBillsByDate({});
      }
    })();
    return () => { cancelled = true; };
  }, [showBills, viewType, currentDate]);

  // Open print planner for a specific date
  const openPrintPlanner = (date) => {
    const dateStr = date instanceof Date ? date.toISOString().split('T')[0] : date;
    setPrintDate(dateStr);
    setShowPrintPlanner(true);
  };

  // Get meetings for the print date (day view)
  const getPrintDayMeetings = () => {
    if (!printDate) return [];
    return meetings.filter(m => m.date === printDate);
  };
  
  // Get all meetings for the month (for monthly snapshot on page 2)
  const getPrintMonthMeetings = () => {
    if (!printDate) return [];
    const targetDate = new Date(printDate + 'T00:00:00');
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    const monthStart = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const monthEnd = `${year}-${String(month + 1).padStart(2, '0')}-31`;
    return meetings.filter(m => m.date >= monthStart && m.date <= monthEnd);
  };

  // Navigation
  const navigate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewType === VIEW_TYPES.DAY) {
      newDate.setDate(newDate.getDate() + direction);
    } else if (viewType === VIEW_TYPES.WEEK) {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (viewType === VIEW_TYPES.MONTH) {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get header text
  const getHeaderText = () => {
    const options = { month: 'long', year: 'numeric' };
    if (viewType === VIEW_TYPES.DAY) {
      return currentDate.toLocaleDateString('en-US', { ...options, day: 'numeric', weekday: 'long' });
    } else if (viewType === VIEW_TYPES.WEEK) {
      const { start, end } = getDateRange();
      const startDate = new Date(start + 'T00:00:00');
      const endDate = new Date(end + 'T00:00:00');
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return currentDate.toLocaleDateString('en-US', options);
  };

  // Filter meetings by facility
  const filteredMeetings = meetings.filter(m => {
    if (selectedFacility === "all") return true;
    if (selectedFacility === "personal") return m.category === "personal";
    return m.facility_id === selectedFacility;
  });

  // Distinct color per facility (auto-assigned if the facility has no color set)
  const facilityColorMap = buildFacilityColorMap(facilities);

  // Set of meeting_ids that overlap at least one other meeting on the same day
  const conflictingIds = findAllConflictingIds(filteredMeetings);

  // Group meetings by date
  const getMeetingsByDate = (dateStr) => {
    return filteredMeetings
      .filter(m => m.date === dateStr)
      .sort((a, b) => a.start_time - b.start_time);
  };

  // Identify the next upcoming meeting (today onward, in chronological order)
  // so the Pre-Meeting Reflection can stay seeded to it — same reflection per
  // meeting until the user clicks "New".
  const nextMeetingId = (() => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const nowHours = now.getHours() + now.getMinutes() / 60;
    const upcoming = filteredMeetings
      .filter(m => m.date > todayStr || (m.date === todayStr && m.end_time > nowHours))
      .sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time))[0];
    return upcoming?.meeting_id || null;
  })();

  // Handle form submission
  const handleSubmit = async () => {
    if (!formData.title || !formData.date) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      const meetingData = {
        title: formData.title,
        date: formData.date,
        start_time: formData.startTime,
        end_time: formData.endTime,
        facility_id: formData.category === "personal" ? null : (formData.facility_id || null),
        category: formData.category,
        recurring: formData.recurring || null,
        recurring_end_date: formData.recurring_end_date || null,
        importance: formData.importance,
        location: formData.location || null,
        notes: formData.notes || null,
        attendees: formData.attendees ? formData.attendees.split(',').map(a => a.trim()) : null
      };

      if (editingMeeting) {
        await axios.patch(`${API}/meetings/${editingMeeting.meeting_id}`, meetingData, { withCredentials: true });
        toast.success("Meeting updated!");
      } else {
        await axios.post(`${API}/meetings`, meetingData, { withCredentials: true });
        toast.success("Meeting created!");
      }

      setShowAddDialog(false);
      setEditingMeeting(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving meeting:", error);
      toast.error("Failed to save meeting");
    }
  };

  const handleDelete = async (meetingId) => {
    if (!confirm("Delete this meeting?")) return;
    try {
      await axios.delete(`${API}/meetings/${meetingId}`, { withCredentials: true });
      toast.success("Meeting deleted");
      setSelectedMeeting(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete meeting");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      date: new Date().toISOString().split('T')[0],
      startTime: 9,
      endTime: 10,
      facility_id: "",
      category: "work",
      recurring: "",
      recurring_end_date: "",
      importance: "medium",
      location: "",
      notes: "",
      attendees: ""
    });
  };

  const openEditDialog = (meeting) => {
    setEditingMeeting(meeting);
    setFormData({
      title: meeting.title,
      date: meeting.date,
      startTime: meeting.start_time,
      endTime: meeting.end_time,
      facility_id: meeting.facility_id || "",
      category: meeting.category || "work",
      recurring: meeting.recurring || "",
      recurring_end_date: meeting.recurring_end_date || "",
      importance: meeting.importance || "medium",
      location: meeting.location || "",
      notes: meeting.notes || "",
      attendees: meeting.attendees?.join(", ") || ""
    });
    setShowAddDialog(true);
  };

  // Get days for current view
  const getDaysForView = () => {
    const days = [];
    const { start, end } = getDateRange();
    const startDate = new Date(start + 'T00:00:00');
    const endDate = new Date(end + 'T00:00:00');
    
    let current = new Date(startDate);
    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Render meeting card
  const MeetingCard = ({ meeting, compact = false }) => {
    const bgColor = getMeetingColor(meeting, facilityColorMap);
    const hasConflict = conflictingIds.has(meeting.meeting_id);

    return (
      <div
        data-testid={hasConflict ? "meeting-card-conflict" : "meeting-card"}
        onClick={() => setSelectedMeeting(meeting)}
        className={`rounded-lg p-2 cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg border-l-4 ${hasConflict ? 'ring-2 ring-red-500/70' : ''}`}
        style={{
          backgroundColor: `${bgColor}20`,
          borderLeftColor: bgColor
        }}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className={`font-medium truncate ${compact ? 'text-xs' : 'text-sm'}`}>
                {meeting.title}
              </p>
              {meeting.source === "certprep" && (
                <Badge data-testid="study-block-badge" className={`${compact ? 'text-[9px] px-1 py-0' : 'text-[10px] px-1.5 py-0'} bg-violet-500/15 border border-violet-500/50 text-violet-300`}>
                  Study
                </Badge>
              )}
              {hasConflict && (
                <Badge
                  data-testid="conflict-badge"
                  className={`${compact ? 'text-[9px] px-1 py-0' : 'text-[10px] px-1.5 py-0'} bg-red-500/15 border border-red-500/60 text-red-300`}
                >
                  <AlertTriangle className={`${compact ? 'w-2 h-2' : 'w-2.5 h-2.5'} mr-0.5`} />
                  Conflict
                </Badge>
              )}
            </div>
            <p className={`text-muted-foreground ${compact ? 'text-[10px]' : 'text-xs'}`}>
              {formatHour(meeting.start_time)} - {formatHour(meeting.end_time)}
            </p>
            {!compact && meeting.facility_info && (
              <Badge
                variant="outline"
                className="mt-1 text-[10px]"
                style={{ borderColor: bgColor, color: bgColor }}
              >
                {meeting.facility_info.short_name || meeting.facility_info.name}
              </Badge>
            )}
          </div>
          {meeting.importance === "high" && (
            <AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0" />
          )}
          {meeting.recurring && (
            <Repeat className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          )}
        </div>
      </div>
    );
  };

  // Day View Component
  const DayView = () => {
    const dateStr = currentDate.toISOString().split('T')[0];
    const dayMeetings = getMeetingsByDate(dateStr);
    const START_HOUR = 6;
    const END_HOUR = 21; // 9 PM (exclusive)
    const SLOT_HEIGHT = 28; // px per 30-min slot
    const HOUR_HEIGHT = SLOT_HEIGHT * 2;
    // Build 30-min slots from START to END
    const slots = [];
    for (let h = START_HOUR; h < END_HOUR; h += 0.5) slots.push(h);

    const laid = layoutDayMeetings(dayMeetings);

    // Compact label: "7 AM", "7:30", "12 PM", "1:30", "9 PM"
    const labelFor = (slot) => {
      const h = Math.floor(slot);
      const isHalf = slot % 1 !== 0;
      const ampm = h >= 12 ? 'PM' : 'AM';
      const displayH = h > 12 ? h - 12 : h === 0 ? 12 : h;
      return isHalf ? `${displayH}:30` : `${displayH} ${ampm}`;
    };

    return (
      <div className="flex flex-col h-[640px]">
        <ScrollArea className="flex-1">
          <div className="relative">
            {/* 30-min grid */}
            {slots.map((slot, i) => {
              const isHalfHour = slot % 1 !== 0;
              return (
                <div
                  key={slot}
                  className={`flex ${isHalfHour ? 'border-t border-dashed border-zinc-800/60' : 'border-t border-zinc-800'}`}
                  style={{ height: `${SLOT_HEIGHT}px` }}
                >
                  <div
                    className={`w-16 px-2 text-[10px] leading-none flex items-start pt-0.5 border-r border-zinc-800 whitespace-nowrap
                      ${isHalfHour ? 'text-zinc-600' : 'text-muted-foreground font-medium'}`}
                  >
                    {labelFor(slot)}
                  </div>
                  <div className="flex-1" />
                </div>
              );
            })}

            {/* Meetings overlay */}
            <div
              className="absolute top-0 left-16 right-0 pointer-events-none"
              style={{ height: `${slots.length * SLOT_HEIGHT}px` }}
              data-testid="day-view-meetings"
            >
              {laid.map(({ meeting, lane, totalLanes }) => {
                const top = (meeting.start_time - START_HOUR) * HOUR_HEIGHT;
                const height = Math.max(
                  (meeting.end_time - meeting.start_time) * HOUR_HEIGHT,
                  SLOT_HEIGHT
                );
                const widthPct = 100 / totalLanes;
                const leftPct = lane * widthPct;
                return (
                  <div
                    key={meeting.meeting_id}
                    className="absolute px-0.5 pointer-events-auto"
                    style={{
                      top: `${top}px`,
                      height: `${height}px`,
                      left: `${leftPct}%`,
                      width: `${widthPct}%`
                    }}
                  >
                    <div className="h-full overflow-hidden">
                      <MeetingCard meeting={meeting} compact={totalLanes > 2} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </ScrollArea>
      </div>
    );
  };

  // Week View Component — full time grid with 30-min intervals, 7 day columns.
  const WeekView = () => {
    const days = getDaysForView();
    const weekDayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const START_HOUR = 6;
    const END_HOUR = 21;
    const SLOT_HEIGHT = 24; // px per 30-min slot
    const HOUR_HEIGHT = SLOT_HEIGHT * 2;
    const slots = [];
    for (let h = START_HOUR; h < END_HOUR; h += 0.5) slots.push(h);

    const labelFor = (slot) => {
      const h = Math.floor(slot);
      const isHalf = slot % 1 !== 0;
      const ampm = h >= 12 ? 'PM' : 'AM';
      const displayH = h > 12 ? h - 12 : h === 0 ? 12 : h;
      return isHalf ? `${displayH}:30` : `${displayH} ${ampm}`;
    };

    return (
      <div className="flex flex-col h-[640px]" data-testid="week-view">
        {/* Day headers — sticky above the scroll region */}
        <div className="flex border-b border-zinc-800">
          <div className="w-16 flex-shrink-0 border-r border-zinc-800" />
          {days.map((day, idx) => (
            <div
              key={idx}
              className={`flex-1 text-center py-2 border-r border-zinc-800 last:border-r-0
                ${isToday(day) ? 'bg-primary/10' : ''}`}
            >
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                {weekDayLabels[day.getDay()]}
              </p>
              <p className={`text-base font-bold ${isToday(day) ? 'text-primary' : ''}`}>
                {day.getDate()}
              </p>
            </div>
          ))}
        </div>

        <ScrollArea className="flex-1">
          <div className="relative">
            {/* Background grid rows */}
            {slots.map((slot) => {
              const isHalfHour = slot % 1 !== 0;
              return (
                <div
                  key={slot}
                  className={`flex ${isHalfHour ? 'border-t border-dashed border-zinc-800/60' : 'border-t border-zinc-800'}`}
                  style={{ height: `${SLOT_HEIGHT}px` }}
                >
                  <div
                    className={`w-16 flex-shrink-0 px-2 text-[10px] leading-none flex items-start pt-0.5 border-r border-zinc-800 whitespace-nowrap
                      ${isHalfHour ? 'text-zinc-600' : 'text-muted-foreground font-medium'}`}
                  >
                    {labelFor(slot)}
                  </div>
                  {/* 7 empty day cells */}
                  {days.map((_, dIdx) => (
                    <div
                      key={dIdx}
                      className="flex-1 border-r border-zinc-800/60 last:border-r-0"
                    />
                  ))}
                </div>
              );
            })}

            {/* Meetings overlay — one column per day */}
            <div
              className="absolute top-0 left-16 right-0 flex pointer-events-none"
              style={{ height: `${slots.length * SLOT_HEIGHT}px` }}
              data-testid="week-view-meetings"
            >
              {days.map((day, dIdx) => {
                const dateStr = day.toISOString().split('T')[0];
                const dayMeetings = getMeetingsByDate(dateStr);
                const laid = layoutDayMeetings(dayMeetings);
                return (
                  <div
                    key={dIdx}
                    className="flex-1 relative border-r border-zinc-800/40 last:border-r-0"
                  >
                    {laid.map(({ meeting, lane, totalLanes }) => {
                      const top = (meeting.start_time - START_HOUR) * HOUR_HEIGHT;
                      const height = Math.max(
                        (meeting.end_time - meeting.start_time) * HOUR_HEIGHT,
                        SLOT_HEIGHT
                      );
                      const widthPct = 100 / totalLanes;
                      const leftPct = lane * widthPct;
                      return (
                        <div
                          key={meeting.meeting_id}
                          className="absolute px-0.5 pointer-events-auto"
                          style={{
                            top: `${top}px`,
                            height: `${height}px`,
                            left: `${leftPct}%`,
                            width: `${widthPct}%`
                          }}
                        >
                          <div className="h-full overflow-hidden">
                            <MeetingCard meeting={meeting} compact />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </ScrollArea>
      </div>
    );
  };

  // Month View Component
  const MonthView = () => {
    const days = getDaysForView();
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Pad start to align with weekday
    const firstDay = days[0].getDay();
    const paddedDays = [...Array(firstDay).fill(null), ...days];
    
    // Pad end to complete the grid
    while (paddedDays.length % 7 !== 0) {
      paddedDays.push(null);
    }
    
    return (
      <div>
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {weekDays.map(day => (
            <div key={day} className="text-center py-2 text-xs text-muted-foreground font-medium">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {paddedDays.map((day, idx) => {
            if (!day) {
              return <div key={idx} className="min-h-[100px] bg-zinc-900/30 rounded-lg" />;
            }
            
            const dateStr = day.toISOString().split('T')[0];
            const dayMeetings = getMeetingsByDate(dateStr);
            const isCurrentMonth = day.getMonth() === currentDate.getMonth();
            
            return (
              <div 
                key={idx}
                className={`min-h-[100px] p-1 rounded-lg border ${
                  isToday(day) 
                    ? 'border-primary bg-primary/5' 
                    : isCurrentMonth 
                      ? 'border-zinc-800 bg-zinc-900/30' 
                      : 'border-zinc-900 bg-zinc-900/10 opacity-50'
                }`}
              >
                <p className={`text-xs font-medium mb-1 ${isToday(day) ? 'text-primary' : ''}`}>
                  {day.getDate()}
                </p>
                <div className="space-y-0.5">
                  {dayMeetings.slice(0, 3).map(meeting => {
                    const color = getMeetingColor(meeting, facilityColorMap);
                    const conflict = conflictingIds.has(meeting.meeting_id);
                    return (
                      <div
                        key={`${meeting.meeting_id}-${dateStr}`}
                        onClick={() => setSelectedMeeting(meeting)}
                        className={`text-[10px] px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80 flex items-center gap-0.5 ${conflict ? 'ring-1 ring-red-500/70' : ''}`}
                        style={{
                          backgroundColor: `${color}30`,
                          color: color
                        }}
                        data-testid={conflict ? "month-meeting-conflict" : "month-meeting"}
                      >
                        {conflict && (
                          <AlertTriangle
                            className="w-2 h-2 flex-shrink-0"
                            style={{ color: CONFLICT_COLOR }}
                          />
                        )}
                        <span className="truncate">{meeting.title}</span>
                      </div>
                    );
                  })}
                  {dayMeetings.length > 3 && (
                    <p className="text-[10px] text-muted-foreground px-1">
                      +{dayMeetings.length - 3} more
                    </p>
                  )}
                  {/* Bill overlay (Personal Budget) — only shown when the
                      Show bills toggle is on. Uses amber to keep clear from
                      meeting/conflict colors. */}
                  {(billsByDate[dateStr] || []).slice(0, 2).map((bill, bIdx) => (
                    <div
                      key={`bill-${dateStr}-${bIdx}`}
                      className="text-[10px] px-1 py-0.5 rounded truncate flex items-center gap-0.5 bg-amber-500/15 text-amber-300 border border-amber-500/30"
                      title={`${bill.name} — $${bill.amount.toFixed(2)}`}
                      data-testid="month-bill"
                    >
                      <span className="text-[9px]">$</span>
                      <span className="truncate">{bill.name}</span>
                    </div>
                  ))}
                  {(billsByDate[dateStr] || []).length > 2 && (
                    <p className="text-[10px] text-amber-400 px-1">
                      +{billsByDate[dateStr].length - 2} more bills
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="calendar-page" className="space-y-6 animate-fade-in">
      {/* Header — stacks vertically on phone, side-by-side from sm+ */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            Calendar
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Manage all your meetings across roles in one place
          </p>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant="outline" 
            size="sm"
            className="h-9"
            onClick={() => openPrintPlanner(currentDate)}
            data-testid="print-day-btn"
            title="Print Day Planner"
          >
            <Printer className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Print Day Planner</span>
            <span className="sm:hidden">Print</span>
          </Button>
          
          <Dialog open={showAddDialog} onOpenChange={(open) => {
            setShowAddDialog(open);
            if (!open) {
              setEditingMeeting(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button size="sm" className="h-9" data-testid="add-meeting-btn">
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Add Meeting</span>
                <span className="sm:hidden">Add</span>
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingMeeting ? "Edit Meeting" : "New Meeting"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Title */}
              <div className="space-y-2">
                <Label>Meeting Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Quality Committee Meeting"
                  data-testid="meeting-title-input"
                />
              </div>
              
              {/* Category (Work vs Personal) */}
              <div className="space-y-2">
                <Label>Category</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={formData.category === "work" ? "default" : "outline"}
                    onClick={() => setFormData({ ...formData, category: "work" })}
                    className="flex-1"
                  >
                    <Briefcase className="w-4 h-4 mr-2" />
                    Work
                  </Button>
                  <Button
                    type="button"
                    variant={formData.category === "personal" ? "default" : "outline"}
                    onClick={() => setFormData({ ...formData, category: "personal", facility_id: "" })}
                    className="flex-1"
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    Personal
                  </Button>
                </div>
              </div>
              
              {/* Facility (only for work) */}
              {formData.category === "work" && facilities.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Facility / Role
                  </Label>
                  <Select
                    value={formData.facility_id}
                    onValueChange={(v) => setFormData({ ...formData, facility_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select facility..." />
                    </SelectTrigger>
                    <SelectContent>
                      {facilities.map(fac => (
                        <SelectItem key={fac.facility_id} value={fac.facility_id}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: fac.color || '#8B5CF6' }}
                            />
                            {fac.name} ({fac.role})
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Date */}
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="meeting-date-input"
                />
              </div>
              
              {/* Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Select
                    value={String(formData.startTime)}
                    onValueChange={(v) => setFormData({ ...formData, startTime: parseFloat(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={String(opt.value)}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Select
                    value={String(formData.endTime)}
                    onValueChange={(v) => setFormData({ ...formData, endTime: parseFloat(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.filter(opt => opt.value > formData.startTime).map(opt => (
                        <SelectItem key={opt.value} value={String(opt.value)}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Recurring */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Repeat className="w-4 h-4" />
                  Repeat
                </Label>
                <Select
                  value={formData.recurring || "none"}
                  onValueChange={(v) => setFormData({ ...formData, recurring: v === "none" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Does not repeat" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Does not repeat</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.recurring && (
                <div className="space-y-2">
                  <Label>Repeat Until (Optional)</Label>
                  <Input
                    type="date"
                    value={formData.recurring_end_date}
                    onChange={(e) => setFormData({ ...formData, recurring_end_date: e.target.value })}
                    min={formData.date}
                  />
                </div>
              )}
              
              {/* Importance */}
              <div className="space-y-2">
                <Label>Importance</Label>
                <div className="flex gap-2">
                  {["low", "medium", "high"].map(level => (
                    <Button
                      key={level}
                      type="button"
                      size="sm"
                      variant={formData.importance === level ? "default" : "outline"}
                      onClick={() => setFormData({ ...formData, importance: level })}
                      className={formData.importance === level && level === "high" ? "bg-red-500/20" : ""}
                    >
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Location */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Location
                </Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g., Conference Room A, Zoom"
                />
              </div>
              
              {/* Attendees */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Attendees
                </Label>
                <Input
                  value={formData.attendees}
                  onChange={(e) => setFormData({ ...formData, attendees: e.target.value })}
                  placeholder="e.g., Dr. Smith, Jane Doe (comma separated)"
                />
              </div>
              
              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes or agenda..."
                  className="min-h-[80px]"
                />
              </div>
              
              <Button onClick={handleSubmit} className="w-full" data-testid="save-meeting-btn">
                {editingMeeting ? "Update Meeting" : "Create Meeting"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Pre-Meeting Reflection — surfaces a leadership prompt to read or
          share before walking into the next meeting. Seeded by the next
          upcoming meeting's id so it stays stable until the user cycles. */}
      <ReflectionCard meetingId={nextMeetingId} />

      {/* Calendar Controls */}
      <Card className="bento-card">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            {/* Navigation */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={goToToday}>
                Today
              </Button>
              <Button variant="outline" size="icon" onClick={() => navigate(1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  fetchData();
                  toast.success("Refreshed");
                }}
                disabled={loading}
                data-testid="calendar-refresh-btn"
                title="Refresh calendar (pulls latest tasks, meetings, bills)"
                aria-label="Refresh calendar"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
              <h2 className="text-lg font-semibold ml-2">{getHeaderText()}</h2>
            </div>
            
            {/* View Type & Filters */}
            <div className="flex items-center gap-2">
              {/* Facility Filter */}
              <Select value={selectedFacility} onValueChange={setSelectedFacility}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Calendars</SelectItem>
                  <SelectItem value="personal">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" />
                      Personal
                    </div>
                  </SelectItem>
                  {facilities.map(fac => (
                    <SelectItem key={fac.facility_id} value={fac.facility_id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: facilityColorMap[fac.facility_id] || '#8B5CF6' }}
                        />
                        {fac.short_name || fac.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* View Type Toggle */}
              <div className="flex border rounded-lg overflow-hidden">
                {Object.entries(VIEW_TYPES).map(([key, value]) => (
                  <Button
                    key={key}
                    variant={viewType === value ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewType(value)}
                    className="rounded-none"
                  >
                    {key.charAt(0) + key.slice(1).toLowerCase()}
                  </Button>
                ))}
              </div>

              {/* Bills overlay toggle (Month view only) */}
              {viewType === VIEW_TYPES.MONTH && (
                <Button
                  variant={showBills ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    const next = !showBills;
                    setShowBills(next);
                    try { localStorage.setItem("niko_show_bills", next ? "1" : "0"); } catch (_) { /* noop */ }
                  }}
                  className={showBills ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}
                  data-testid="toggle-bills-btn"
                  title="Overlay upcoming bills onto the month grid"
                >
                  $ Bills
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Legend */}
      {facilities.length > 0 && (
        <div className="flex items-center gap-4 flex-wrap" data-testid="calendar-legend">
          <span className="text-sm text-muted-foreground">Legend:</span>
          {facilities.map(fac => (
            <div key={fac.facility_id} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: facilityColorMap[fac.facility_id] || '#8B5CF6' }}
              />
              <span className="text-sm">{fac.short_name || fac.name}</span>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PERSONAL_COLOR }} />
            <span className="text-sm">Personal</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" style={{ color: CONFLICT_COLOR }} />
            <span className="text-sm">Conflict (overlap)</span>
          </div>
        </div>
      )}
      
      {/* Calendar View */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <Card className="bento-card">
            <CardContent className="pt-4">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {viewType === VIEW_TYPES.DAY && <DayView />}
                  {viewType === VIEW_TYPES.WEEK && <WeekView />}
                  {viewType === VIEW_TYPES.MONTH && <MonthView />}
                </>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Right Sidebar - AOC */}
        <div className="lg:col-span-1">
          <AOCManager facilities={facilities} onUpdate={fetchData} />
        </div>
      </div>
      
      {/* Meeting Detail Dialog */}
      <Dialog open={!!selectedMeeting} onOpenChange={(open) => !open && setSelectedMeeting(null)}>
        <DialogContent>
          {selectedMeeting && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: getMeetingColor(selectedMeeting, facilityColorMap) }}
                  />
                  {selectedMeeting.title}
                  {conflictingIds.has(selectedMeeting.meeting_id) && (
                    <Badge
                      data-testid="detail-conflict-badge"
                      className="ml-2 bg-red-500/15 border border-red-500/60 text-red-300 text-[10px]"
                    >
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Conflict
                    </Badge>
                  )}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span>
                    {new Date(selectedMeeting.date + 'T00:00:00').toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                  <span className="text-muted-foreground">|</span>
                  <span>
                    {formatHour(selectedMeeting.start_time)} - {formatHour(selectedMeeting.end_time)}
                  </span>
                </div>
                
                {selectedMeeting.facility_info && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedMeeting.facility_info.name}</span>
                  </div>
                )}
                
                {selectedMeeting.location && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedMeeting.location}</span>
                  </div>
                )}
                
                {selectedMeeting.attendees?.length > 0 && (
                  <div className="flex items-start gap-2 text-sm">
                    <Users className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <span>{selectedMeeting.attendees.join(", ")}</span>
                  </div>
                )}
                
                {selectedMeeting.recurring && (
                  <div className="flex items-center gap-2 text-sm">
                    <Repeat className="w-4 h-4 text-muted-foreground" />
                    <span className="capitalize">Repeats {selectedMeeting.recurring}</span>
                    {selectedMeeting.recurring_end_date && (
                      <span className="text-muted-foreground">
                        until {new Date(selectedMeeting.recurring_end_date + 'T00:00:00').toLocaleDateString()}
                      </span>
                    )}
                  </div>
                )}
                
                {selectedMeeting.notes && (
                  <div className="p-3 rounded-lg bg-zinc-800/50 text-sm">
                    {selectedMeeting.notes}
                  </div>
                )}
                
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      openEditDialog(selectedMeeting);
                      setSelectedMeeting(null);
                    }}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    onClick={() => handleDelete(selectedMeeting.meeting_id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Printable Day Planner Modal */}
      {showPrintPlanner && (
        <PrintableDayPlanner
          date={printDate}
          meetings={getPrintMonthMeetings()}
          tasks={tasks}
          facilities={facilities}
          facilityColorMap={facilityColorMap}
          userName={user?.name || "Leader"}
          onClose={() => setShowPrintPlanner(false)}
        />
      )}
    </div>
  );
}
